/**
 * Renders the built-in AskUserQuestion tool as interactive option cards.
 *
 * Each question is displayed with its header and a list of radio-style options.
 * When there's a single question, clicking an option submits immediately.
 * When there are multiple questions, the user must answer all of them before
 * a "Submit" button appears to send all answers at once.
 *
 * Uses Deckard's theme palette tokens for proper light/dark mode support.
 */
import { Button } from '@components/button';
import { Typography } from '@components/typography';
import { Box, Stack, useTheme } from '@mui/material';
import { useState } from 'react';
import type { RichBlockProps } from './richBlockTypes';

export const AskUserQuestionBlock = ({ block, onRespond }: RichBlockProps) => {
  const { palette } = useTheme();
  const [selectedMap, setSelectedMap] = useState<Record<string, string>>({});
  // Only block interaction when explicitly answered — expired blocks remain
  // clickable so the user can re-send their selection as a new message.
  const isAnswered = block.status === 'answered';
  const questions =
    (block.payload.questions as Array<{
      question: string;
      header: string;
      options: Array<{ label: string; description: string }>;
      multiSelect: boolean;
    }>) ?? [];

  const isSingleQuestion = questions.length === 1;
  const allAnswered = questions.every((q) => selectedMap[q.question]);

  const handleSelect = (question: string, label: string) => {
    if (isAnswered) return;

    const updated = { ...selectedMap, [question]: label };
    setSelectedMap(updated);

    // Single question: submit immediately on click
    if (isSingleQuestion) {
      onRespond?.(block.blockId, { answers: updated });
    }
  };

  const handleSubmitAll = () => {
    if (isAnswered || !allAnswered) return;
    onRespond?.(block.blockId, { answers: selectedMap });
  };

  return (
    <Stack mt={1} mb={2} gap={2} data-testid="rich-block-ask-user-question">
      {questions.map((q) => (
        <Stack key={q.question} gap={1}>
          <Typography variant="subtitle1" color="agentChat.message.agent.foreground.default">
            {q.question}
          </Typography>
          <Stack gap={1}>
            {q.options.map((opt) => {
              const isSelected =
                selectedMap[q.question] === opt.label ||
                (block.response as Record<string, Record<string, string>> | undefined)?.answers?.[
                  q.question
                ] === opt.label;

              return (
                <Stack
                  key={opt.label}
                  role="button"
                  onClick={() => handleSelect(q.question, opt.label)}
                  sx={{
                    p: 1.5,
                    cursor: isAnswered ? 'default' : 'pointer',
                    opacity: isAnswered && !isSelected ? 0.5 : 1,
                    border: '1px solid',
                    borderColor: isSelected ? palette.primary.dark : palette.input.border.default,
                    bgcolor: isSelected
                      ? palette.agentChat.message.agent.backgroundSelected
                      : palette.background.paper,
                    borderRadius: 1,
                    transition: 'all 0.15s',
                    display: 'flex',
                    gap: 1,
                    '.agent-chat-ask-user-question-block-label': {
                      color: isSelected
                        ? palette.foreground.paper.default
                        : palette.agentChat.message.agent.foreground.default,
                    },
                    '.agent-chat-ask-user-question-block-description': {
                      color: isSelected
                        ? palette.foreground.paper.default
                        : palette.agentChat.message.agent.foreground.light,
                    },
                    '&:hover': isAnswered
                      ? {}
                      : {
                          bgcolor: palette.agentChat.message.agent.backgroundHover,
                        },
                  }}
                  data-testid={`ask-option-${opt.label}`}
                >
                  <Typography variant="body1" className="agent-chat-ask-user-question-block-label">
                    {opt.label}
                  </Typography>
                  <Typography
                    variant="caption2"
                    className="agent-chat-ask-user-question-block-description"
                  >
                    {opt.description}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      ))}

      {/* Multi-question: show submit button once all questions are answered */}
      {!isSingleQuestion && !isAnswered && (
        <Box sx={{ mt: 1 }}>
          <Button
            variant="primary"
            fullWidth
            size="small"
            disabled={!allAnswered}
            onClick={handleSubmitAll}
            data-testid="ask-submit-btn"
          >
            Submit Answers
          </Button>
        </Box>
      )}
    </Stack>
  );
};
